import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import React, { useCallback, useEffect, useState } from "react";
import { Outlet } from "react-router";
import { useLayoutContext } from "@ntg/component-library/contexts";
import ProcessInstanceList from "./ProcessInstanceList";
import ProcessTriggerPanel from "./ProcessTriggerPanel";
import ProcessDetailDialog from "./ProcessDetailDialog";
import { useOrchestrationInstances } from "../hooks/Starters/useOrchestrationInstances";
import { useProcessMonitorStore } from "../stores/useProcessMonitorStore";
import { IOrchestrationInstanceSummary } from "../declarations/IOrchestrationInstance";

export default function ProcessMonitorPage() {
    const { SetPrimaryTitle, SetSecondaryHeader } = useLayoutContext();
    const setActiveCount = useProcessMonitorStore(
        (state) => state.setActiveCount,
    );

    useEffect(() => {
        SetPrimaryTitle("Process Monitor");
        return () => {
            SetPrimaryTitle("");
            SetSecondaryHeader(null);
        };
    }, [SetPrimaryTitle, SetSecondaryHeader]);

    const [statusFilter, setStatusFilter] = useState<string[]>([]);
    const [nameFilter, setNameFilter] = useState("");
    const [allInstances, setAllInstances] = useState<
        IOrchestrationInstanceSummary[]
    >([]);
    const [continuationToken, setContinuationToken] = useState<string | null>(
        null,
    );

    const { data, isLoading } = useOrchestrationInstances({
        status: statusFilter.length === 1 ? statusFilter[0] : undefined,
        name: nameFilter || undefined,
        pageSize: 50,
    });

    // Reset accumulated instances when filters change
    useEffect(() => {
        if (data) {
            setAllInstances(data.instances);
            setContinuationToken(data.continuationToken);
        }
    }, [data]);

    // Sync active count to store
    useEffect(() => {
        const activeCount = allInstances.filter(
            (i) => i.runtimeStatus === "Running" || i.runtimeStatus === "Pending",
        ).length;
        setActiveCount(activeCount);
    }, [allInstances, setActiveCount]);

    // Load more with continuation token
    const { data: moreData } = useOrchestrationInstances({
        status: statusFilter.length === 1 ? statusFilter[0] : undefined,
        name: nameFilter || undefined,
        pageSize: 50,
        continuationToken: continuationToken ?? undefined,
    });

    const handleLoadMore = useCallback(() => {
        if (moreData && continuationToken) {
            setAllInstances((prev) => {
                const existingIds = new Set(prev.map((i) => i.instanceId));
                const newInstances = moreData.instances.filter(
                    (i) => !existingIds.has(i.instanceId),
                );
                return [...prev, ...newInstances];
            });
            setContinuationToken(moreData.continuationToken);
        }
    }, [moreData, continuationToken]);

    // Filter by multiple statuses client-side
    const filteredInstances =
        statusFilter.length > 1
            ? allInstances.filter((i) =>
                  statusFilter.includes(i.runtimeStatus),
              )
            : allInstances;

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <ProcessTriggerPanel />

            <Box sx={{ mt: 2 }}>
                <ProcessInstanceList
                    continuationToken={data?.continuationToken ?? null}
                    instances={filteredInstances}
                    isLoading={isLoading}
                    nameFilter={nameFilter}
                    onLoadMore={handleLoadMore}
                    onNameFilterChange={setNameFilter}
                    onStatusFilterChange={setStatusFilter}
                    statusFilter={statusFilter}
                />
            </Box>

            <ProcessDetailDialog />
            <Outlet />
        </Container>
    );
}
